if (!customElements.get('gb-test-chart')) {
  let wrappingSelectorId = 0;
  customElements.define('gb-test-chart', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.curves = Array.from(this.querySelectorAll('[data-role="data-curves"] [data-series]'));
      this.selector = this.querySelector('[data-chart-select]');
      this.status = this.querySelector('[data-chart-status]');
      if (this.curves.length === 0 || !this.selector || !this.status) return;
      this.seriesOptions = Array.from(this.selector.options).filter(option => option.value !== 'all');
      if (this.seriesOptions.length !== this.curves.length || !this.seriesOptions.every(option => this.curves.some(curve => curve.dataset.series === option.value))) return;
      // Recover the legend order if this element reconnects after a highlighted curve was moved.
      if (this.hasAttribute('data-wrap-chart-selector')) {
        this.curves = this.seriesOptions.map(option => this.curves.find(curve => curve.dataset.series === option.value));
      }
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-chart-all]');
        if (!button || !this.contains(button)) return;
        this.selectSeries('all');
        this.closeWrappingSelector();
      }, {signal});
      this.selector.addEventListener('change', () => this.selectSeries(this.selector.value), {signal});
      if (this.hasAttribute('data-wrap-chart-selector')) this.createWrappingSelector(signal);
      this.classList.add('is-ready');
      this.selectSeries('all');
    }
    createWrappingSelector(signal) {
      if (this.wrappingSelector || this.selector.disabled) return;
      const label = Array.from(this.selector.labels || []).find(item => this.contains(item));
      if (!label) return;
      const document = this.ownerDocument;
      const baseId = `${this.selector.id || 'gb-chart'}-wrapping-${++wrappingSelectorId}`;
      this.wrappingLabel = label;
      if (!label.id) {
        label.id = `${baseId}-label`;
        this.generatedLabelId = label.id;
      }
      const details = document.createElement('details');
      details.className = 'gb-wrapping-selector';
      details.setAttribute('data-chart-wrapping-selector', '');
      const summary = document.createElement('summary');
      summary.className = 'gb-wrapping-selector-trigger';
      const selectedLabel = document.createElement('span');
      selectedLabel.id = `${baseId}-value`;
      selectedLabel.setAttribute('data-chart-selected-label', '');
      summary.setAttribute('aria-labelledby', `${label.id} ${selectedLabel.id}`);
      summary.appendChild(selectedLabel);
      const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      chevron.setAttribute('width', '12');
      chevron.setAttribute('height', '8');
      chevron.setAttribute('viewBox', '0 0 12 8');
      chevron.setAttribute('aria-hidden', 'true');
      chevron.setAttribute('focusable', 'false');
      const chevronPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      chevronPath.setAttribute('d', 'm1 1 5 5 5-5');
      chevronPath.setAttribute('fill', 'none');
      chevronPath.setAttribute('stroke', 'currentColor');
      chevronPath.setAttribute('stroke-width', '2');
      chevron.appendChild(chevronPath);
      summary.appendChild(chevron);
      const list = document.createElement('ul');
      list.className = 'gb-wrapping-selector-options';
      list.setAttribute('aria-labelledby', label.id);
      Array.from(this.selector.options).forEach(option => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.chartChoice = option.value;
        button.textContent = option.textContent.trim();
        button.disabled = option.disabled || Boolean(option.parentElement.disabled);
        button.setAttribute('aria-pressed', 'false');
        item.appendChild(button);
        list.appendChild(item);
      });
      details.append(summary, list);
      this.selector.before(details);
      this.wrappingSelector = details;
      this.wrappingSummary = summary;
      this.wrappingSelectedLabel = selectedLabel;
      this.wrappingChoices = Array.from(list.querySelectorAll('[data-chart-choice]'));
      this.mobileQuery = document.defaultView.matchMedia('(max-width: 599px)');
      details.addEventListener('click', event => {
        const choice = event.target.closest('[data-chart-choice]');
        if (!choice || !details.contains(choice) || choice.disabled || !this.mobileQuery.matches) return;
        this.selectSeries(choice.dataset.chartChoice);
        this.closeWrappingSelector(true);
      }, {signal});
      details.addEventListener('keydown', event => {
        if (!this.mobileQuery.matches) return;
        if (event.key === 'Escape' && details.open) {
          event.preventDefault();
          event.stopPropagation();
          this.closeWrappingSelector(true);
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const choices = this.wrappingChoices.filter(button => !button.disabled);
        if (!choices.length) return;
        const index = choices.indexOf(event.target.closest('[data-chart-choice]'));
        if (index < 0 && !summary.contains(event.target)) return;
        event.preventDefault();
        details.open = true;
        let next;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = choices.length - 1;
        else if (index < 0) next = event.key === 'ArrowDown' ? 0 : choices.length - 1;
        else next = (index + (event.key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length;
        choices[next].focus();
      }, {signal});
      details.addEventListener('focusout', event => {
        // WebKit may blur to the body before a pointer click without focusing the button.
        // Only a known focus destination outside the disclosure is keyboard departure.
        if (event.relatedTarget && !details.contains(event.relatedTarget)) this.closeWrappingSelector();
      }, {signal});
      document.defaultView.addEventListener('blur', () => this.closeWrappingSelector(), {signal});
      document.addEventListener('pointerdown', event => {
        if (!details.contains(event.target) && !label.contains(event.target)) this.closeWrappingSelector();
      }, {signal});
      label.addEventListener('click', event => {
        if (!this.mobileQuery.matches) return;
        event.preventDefault();
        details.open = !details.open;
        summary.focus({preventScroll: true});
      }, {signal});
      this.mobileQuery.addEventListener('change', () => {
        const active = document.activeElement;
        const wasWrappingControl = details.contains(active);
        const wasNativeSelector = active === this.selector;
        this.closeWrappingSelector();
        if (this.mobileQuery.matches && wasNativeSelector) summary.focus({preventScroll: true});
        else if (!this.mobileQuery.matches && wasWrappingControl) this.selector.focus({preventScroll: true});
      }, {signal});
      this.classList.add('has-wrapping-selector');
    }
    closeWrappingSelector(restoreFocus = false) {
      if (!this.wrappingSelector) return;
      this.wrappingSelector.open = false;
      if (restoreFocus && this.mobileQuery?.matches) this.wrappingSummary.focus({preventScroll: true});
    }
    selectSeries(id) {
      const selected = this.seriesOptions.find(option => option.value === id);
      if (id !== 'all' && !selected) return;
      this.dataset.selectedSeries = id;
      this.curves.forEach(curve => {
        const active = curve.dataset.series === id;
        curve.classList.toggle('is-muted', id !== 'all' && !active);
        curve.classList.toggle('is-selected', active);
      });
      if (selected) {
        const curve = this.curves.find(item => item.dataset.series === id);
        curve.parentNode.appendChild(curve);
      } else {
        this.curves.forEach(curve => curve.parentNode.appendChild(curve));
      }
      this.selector.value = id;
      this.status.textContent = selected
        ? this.status.dataset.highlightText.replace('__compound__', selected.textContent.trim())
        : this.status.dataset.allText;
      if (this.wrappingSelector) {
        this.wrappingSelectedLabel.textContent = this.selector.selectedOptions[0]?.textContent.trim() || '';
        this.wrappingChoices.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.chartChoice === id)));
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.wrappingSelector?.remove();
      if (this.generatedLabelId && this.wrappingLabel?.id === this.generatedLabelId) this.wrappingLabel.removeAttribute('id');
      this.classList.remove('has-wrapping-selector');
      this.wrappingSelector = null;
      this.wrappingSummary = null;
      this.wrappingSelectedLabel = null;
      this.wrappingChoices = null;
      this.wrappingLabel = null;
      this.generatedLabelId = null;
      this.mobileQuery = null;
    }
  });
}
